import React, { useState } from 'react';
import { getAuthToken } from '../../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SocialModule = ({ user }) => {
  const [activePlatform, setActivePlatform] = useState('instagram');
  const [formData, setFormData] = useState({
    topic: '',
    goal: 'engagement',
    tone: 'casual',
    includeHashtags: true,
    includeEmojis: true
  });
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');

  const platforms = [
    { id: 'instagram', name: 'Instagram', icon: '📸', color: 'pink', maxChars: 2200 },
    { id: 'facebook', name: 'Facebook', icon: '👤', color: 'blue', maxChars: 63206 },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: 'cyan', maxChars: 3000 },
    { id: 'twitter', name: 'X (Twitter)', icon: '🐦', color: 'gray', maxChars: 280 },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', color: 'purple', maxChars: 2200 },
    { id: 'pinterest', name: 'Pinterest', icon: '📌', color: 'red', maxChars: 500 }
  ];

  const contentTypes = [
    { id: 'post', name: 'Publication standard' },
    { id: 'carousel', name: 'Carousel (multi-slides)' },
    { id: 'story', name: 'Story' },
    { id: 'reel', name: 'Reel/Short' },
    { id: 'thread', name: 'Thread' }
  ];

  const goals = [
    { id: 'engagement', name: 'Engagement', emoji: '💬' },
    { id: 'awareness', name: 'Notoriété', emoji: '📢' },
    { id: 'traffic', name: 'Trafic', emoji: '🔗' },
    { id: 'sales', name: 'Ventes', emoji: '💰' },
    { id: 'community', name: 'Communauté', emoji: '👥' }
  ];

  const handleGenerate = async () => {
    if (!formData.topic) return;
    setGenerating(true);
    
    try {
      const token = getAuthToken();
      const platform = platforms.find(p => p.id === activePlatform);
      
      const response = await fetch(`${API_URL}/api/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content_type: 'social',
          topic: formData.topic,
          tone: formData.tone,
          word_count: Math.min(platform.maxChars / 5, 500),
          additional_instructions: `
            Plateforme: ${platform.name}
            Limite caractères: ${platform.maxChars}
            Objectif: ${formData.goal}
            Inclure hashtags: ${formData.includeHashtags ? 'Oui, 5-10 hashtags pertinents' : 'Non'}
            Inclure emojis: ${formData.includeEmojis ? 'Oui, utiliser des emojis pertinents' : 'Non'}
            
            Créer un post viral et engageant pour ${platform.name}.
            - Hook puissant en première ligne
            - Contenu de valeur
            - Call-to-action clair
            - Format optimisé pour ${platform.name}
          `
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedContent(data.content);
      }
    } catch (error) {
      console.error('Social generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  const currentPlatform = platforms.find(p => p.id === activePlatform);

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Platforms */}
      <div className="col-span-2">
        <h3 className="text-white font-semibold mb-4">Plateformes</h3>
        <div className="space-y-2">
          {platforms.map(platform => (
            <button
              key={platform.id}
              onClick={() => setActivePlatform(platform.id)}
              className={`w-full p-3 rounded-xl transition-all flex items-center space-x-3 ${
                activePlatform === platform.id
                  ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30'
                  : 'bg-[#12121a] border border-gray-800 hover:border-gray-700'
              }`}
            >
              <span className="text-xl">{platform.icon}</span>
              <span className={activePlatform === platform.id ? 'text-pink-400' : 'text-white'}>
                {platform.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Configuration */}
      <div className="col-span-5 bg-[#12121a] rounded-2xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-white font-semibold">Configuration {currentPlatform?.name}</h3>
          <span className="text-xs text-gray-500">{currentPlatform?.maxChars} caractères max</span>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Sujet / Thème *</label>
            <textarea
              value={formData.topic}
              onChange={(e) => setFormData({...formData, topic: e.target.value})}
              placeholder="De quoi voulez-vous parler ? Ex: Lancement de notre nouvelle collection été..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-pink-500 transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Objectif</label>
            <div className="flex flex-wrap gap-2">
              {goals.map(goal => (
                <button
                  key={goal.id}
                  onClick={() => setFormData({...formData, goal: goal.id})}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${
                    formData.goal === goal.id
                      ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {goal.emoji} {goal.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.includeHashtags}
                onChange={(e) => setFormData({...formData, includeHashtags: e.target.checked})}
                className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-pink-500 focus:ring-pink-500"
              />
              <span className="text-sm text-gray-400">Inclure hashtags</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.includeEmojis}
                onChange={(e) => setFormData({...formData, includeEmojis: e.target.checked})}
                className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-pink-500 focus:ring-pink-500"
              />
              <span className="text-sm text-gray-400">Inclure emojis</span>
            </label>
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating || !formData.topic}
            className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 rounded-xl font-semibold text-white transition-all"
          >
            {generating ? 'Création du post...' : `Créer le post ${currentPlatform?.name}`}
          </button>
        </div>
      </div>

      {/* Output */}
      <div className="col-span-5 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white font-semibold">Aperçu {currentPlatform?.name}</h3>
          {generatedContent && (
            <button
              onClick={() => navigator.clipboard.writeText(generatedContent)}
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Copier
            </button>
          )}
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          {generating ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="w-12 h-12 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p>Création du post viral...</p>
              </div>
            </div>
          ) : generatedContent ? (
            <div className="bg-gray-800/50 rounded-xl p-4">
              <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">{generatedContent}</div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <span className="text-4xl mb-4 block">{currentPlatform?.icon}</span>
                <p>Votre post {currentPlatform?.name}<br/>apparaîtra ici</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialModule;
